import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IVideo } from "../hooks/useFetchVideos";
import { IBrand } from "../../../brand/team/data/type";
import { VideoCardMenu } from "../../../../components/videoSection/VideoCardMenu";
import { StatusPill, FlagChip } from "../../../../components/videoSection/StatusPill";
import {
  getVideoStatus,
  getVideoFilename,
  isDemo,
  isNonProject,
} from "../../../../components/videoSection/videoStatus";
import { setPageTitle } from "../../../../components/PageTitle";
import { Breadcrumbs, buildProjectTrail } from "../../../../components/Breadcrumbs";
import type { ProjectTrailInput } from "../../../../components/Breadcrumbs";
import { Button } from "../../../../components/Button/Button";
import { IconGoToTab } from "../../../../utils/icons";
import { cldUrl } from "../../../../utils/cloudinary";
import { getVideoDatesForDisplay } from "../../../videoProduction/helper";
import { fancyTimeFormat } from "../adminVideosV2/helper";
import { DownloadBtn } from "./DownloadBtn";
import { PanelVideo } from "./PanelVideo";
import { formatName } from "./helpers";
import { FileSizeSpec } from "../../../../components/videoSection/FileSizeSpec";
import {
  groupVideoOptions,
  getVideoChannelLabel,
  getVideoContentTypeLabel,
  formatFps,
  formatVideoFileSize,
} from "../../../../components/videoSection/videoDetails";
import { useUser } from "../../../../hooks/useUser";
import { isInternal } from "../../../../utils";
import { usePortalPath } from "../../../../hooks/usePortalPath";

export interface PanelCounts {
  stories?: number;
  thumbnails?: number;
  transcripts?: number;
  pages?: number;
}

interface Props {
  video: IVideo;
  onClose?: () => void;
  counts?: PanelCounts;
  /**
   * Nested host (§04): the expert profile and product detail already own a
   * title and a breadcrumb, so both are suppressed here and identity lives in
   * `NestedModuleBar` instead. Only the player, the metadata card and the jump
   * rail render.
   */
  nested?: boolean;
  /** The video's project, fetched by the page (null until loaded / none). */
  project?: any;
  menuProps: {
    video: IVideo;
    brand: IBrand;
    refetch: () => void;
    onDeleted?: () => void;
    onSettingsClosed?: () => void;
  };
}

const MICRO = "font-CircularStdBlack uppercase";
const MICRO_STYLE = { fontSize: 9.5, letterSpacing: ".12em" } as const;

const MetaCell: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <div className={`${MICRO} text-accent-text`} style={MICRO_STYLE}>
      {label}
    </div>
    <div className="min-w-0" style={{ marginTop: 8, fontSize: 13.5 }}>
      {children}
    </div>
  </div>
);

const EmptyValue: FC<{ children?: ReactNode }> = ({ children }) => (
  <span className="text-muted">{children || "—"}</span>
);

/** The page header: breadcrumb, then the player beside the identity column. */
export function Header({ video, onClose, counts, menuProps, nested = false, project }: Props) {
  const navigate = useNavigate();
  const { p, basePath } = usePortalPath();
  const { role } = useUser();
  const projectId = video.projectId ?? video.project?.id;
  const title = formatName(video);
  const status = getVideoStatus(video);
  const filename = getVideoFilename(video);

  useEffect(() => {
    // Nested hosts own the tab title. Writing it here left the expert and product
    // pages named after a video the user had already closed: nothing restores it,
    // because both set their title inside a fetch callback that adding or removing
    // `?video=` does not re-run.
    if (nested) return;
    setPageTitle(title);
  }, [title, nested]);

  // The project (loaded once by the page): the trail needs its title, and
  // the Download button its brands' pre-approval setting.
  const breadcrumbProject: ProjectTrailInput = project || null;

  const product = video.videoProducts?.[0];
  // For internal roles `/products` resolves to the flat AllProducts list, which never
  // reads `?productId=`; only the brand-scoped `/products/:brandId` opens the detail.
  // VideoCard branches on this — the header had dropped the branch, so the Product
  // cell was a dead link for every internal user.
  const openProduct = (item: any) => {
    if (!item?.desireid) return;
    const brandId = menuProps.brand?.id ?? (video as any).project?.brand_id;
    if (isInternal(role) && brandId) {
      navigate(p(`/products/${brandId}?productId=${item.desireid}`));
    } else {
      navigate(p(`/products?productId=${item.desireid}`));
    }
  };
  const experts = video.videoPros || [];
  const { deliveredDate, approvalDate } = getVideoDatesForDisplay(video);
  const contentTypeLabel = getVideoContentTypeLabel(video);
  const channelLabel = getVideoChannelLabel(video);
  // The project's additional options this cut was made for (watermark
  // variants, caption languages). Usually empty — the row only renders when a
  // project asked for variants, each of which is its own deliverable file.
  const optionGroups = useMemo(() => groupVideoOptions(video), [video]);

  const fileLine = useMemo(
    () =>
      [
        video.version ? `v${video.version}` : "",
        video.aspect_ratio ? video.aspect_ratio.replace(/x/i, "×") : "",
        formatFps(video.fps),
        video.length ? fancyTimeFormat(video.length) : "",
      ]
        .filter(Boolean)
        .join("  ·  "),
    [video],
  );

  // Anchor scroll only — `?selectedTab=` stays vestigial, no URL parameter.
  // Order mirrors the DOM, which now leads with Page score: a rail that lists
  // the sections in a different order than the page stacks them sends every
  // chip to the wrong-looking place.
  const jump = [
    { id: "panel-page-score", label: "Page score", count: counts?.pages },
    { id: "panel-performance", label: "Performance" },
    { id: "panel-stories", label: "Stories", count: counts?.stories },
    { id: "panel-thumbnails", label: "Thumbnails", count: counts?.thumbnails },
    { id: "panel-transcripts", label: "Transcripts", count: counts?.transcripts },
  ];

  // The active chip follows the scroll position. The margins narrow the
  // observation band to the top of the viewport, so "active" means the section
  // you are reading rather than any section merely on screen. The seed is the
  // FIRST panel in the DOM — the observer only fires once something intersects,
  // so at scroll top the rail would otherwise highlight nothing.
  const [activeChip, setActiveChip] = useState("panel-page-score");
  useEffect(() => {
    const nodes = jump
      .map((chip) => document.getElementById(chip.id))
      .filter(Boolean) as HTMLElement[];
    if (nodes.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const topMost = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (topMost) setActiveChip(topMost.target.id);
      },
      { rootMargin: "-96px 0px -55% 0px", threshold: 0 },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
    // Panels mount with the page but resolve their counts later; re-observing
    // when a count lands costs nothing and covers any late mount.
  }, [counts?.stories, counts?.thumbnails, counts?.transcripts, counts?.pages]);

  const goToPanel = (id: string) => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
  };

  return (
    <div className="flex flex-col gap-5">
      {!nested && projectId && breadcrumbProject && (
        <Breadcrumbs
          items={buildProjectTrail(breadcrumbProject, {
            projectHref: p(`/video-production/projects/${projectId}?tab=details`),
            basePath,
          }).concat({ label: title })}
        />
      )}

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <PanelVideo video={video} compact={nested} />

        <div className="flex-1 min-w-0 flex flex-col gap-3.5 w-full">
          <div>
            <div className={`flex items-center flex-wrap gap-2 ${nested ? "hidden" : ""}`}>
              {/* KEPT, against the handoff, which removed it. The breadcrumb is
                  not a back path — it renders only when the video has a project
                  AND that request succeeds, and its crumbs go to the SOW, the
                  order and the project, none of which is the list you came from.
                  On the expert and product hosts this page is nested and back
                  means "close ?video= and return to the grid in place". */}
              {onClose && (
                <button
                  type="button"
                  aria-label="Back"
                  onClick={onClose}
                  className="w-8 h-8 shrink-0 border border-border-token rounded-lg bg-surface text-text-2 inline-flex items-center justify-center hover:text-text-1 hover:border-accent transition-colors"
                >
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m14 6-6 6 6 6" />
                  </svg>
                </button>
              )}
              <h1
                className="m-0 font-CircularStdBlack text-text-1 text-[24px] md:text-[28px]"
                style={{ lineHeight: 1.15 }}
              >
                {title}
              </h1>
              <StatusPill status={status.key} label={status.label} />
              {isDemo(video) && <FlagChip gold label="Demo" />}
              {isNonProject(video) && <FlagChip label="Non-project" />}
            </div>
            {filename && (
              // The disambiguator: `formatVideoName` collapses a name to its
              // first underscore segment, so three deliverables all read "Hero".
              <div
                className="text-text-2 tabular-nums truncate"
                style={{ fontSize: 11.5, marginTop: 6 }}
                title={filename}
              >
                {filename}
              </div>
            )}
          </div>

          <div className={`flex-wrap items-center gap-2 ${nested ? "hidden" : "flex"}`}>
            {projectId && (
              <Button
                variant="soft"
                color="default"
                icon={<IconGoToTab width={16} height={16} stroke="currentColor" />}
                onClick={() => navigate(p(`/video-production/projects/${projectId}?tab=downloads`))}
                className="!min-w-0 !h-10 gap-2"
              >
                View in Project
              </Button>
            )}
            {/* The project carries its brands' pre-approval setting: a file
                owned through its project has no brandOwners of its own. */}
            <DownloadBtn video={video} variant="secondary" project={breadcrumbProject || undefined} />
            <VideoCardMenu
              video={video}
              context={{ reviewButton: "label" }}
              refetch={menuProps.refetch}
              brand={menuProps.brand}
              title={title}
              onDeleted={menuProps.onDeleted}
              onSettingsClosed={menuProps.onSettingsClosed}
            />
          </div>

          {/* Four groups in a 2x2 card, replacing six unlike cells in one
              wrapping row divided by 1px rules that dangled once it wrapped. */}
          <div
            className="bg-surface border border-border-token rounded-xl grid grid-cols-1 sm:grid-cols-2 items-start"
            style={
              nested
                ? { padding: 14, gap: "14px 18px", boxShadow: "var(--sh-1)" }
                : { padding: 16, gap: "16px 20px", boxShadow: "var(--sh-1)" }
            }
          >
            <MetaCell label="Product">
              {product?.title ? (
                <button
                  type="button"
                  onClick={() => openProduct(product)}
                  className="flex items-start gap-2.5 min-w-0 text-left text-text-1 hover:text-accent-text"
                >
                  <span className="w-[26px] h-[26px] shrink-0 rounded-md bg-surface-2 border border-border-token overflow-hidden inline-flex items-center justify-center">
                    {/* The PRODUCT's image. The old header passed the VIDEO's
                        thumbnail here, so this square showed the same frame as
                        the player above it. */}
                    {product.image && (
                      <img src={product.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                    )}
                  </span>
                  {/* Full name, wrapped — product titles are long and the cell
                      has the room; the grid's items-start absorbs the height. */}
                  <span className="min-w-0 break-words leading-[1.35]" style={{ paddingTop: 4 }}>
                    {product.title}
                  </span>
                </button>
              ) : (
                <EmptyValue>No product linked</EmptyValue>
              )}
            </MetaCell>

            <MetaCell label="Experts">
              {experts.length === 0 ? (
                <EmptyValue />
              ) : (
                <div className="flex flex-col items-start gap-[7px] min-w-0">
                  {experts.slice(0, 3).map((person) => (
                    <button
                      key={person.userid || person.displayName}
                      type="button"
                      onClick={() =>
                        navigate(
                          p(`/experts/${person.userid}/${person.displayName}?proId=${person.userid}`),
                        )
                      }
                      className="flex items-center gap-2 min-w-0 text-left text-text-1 hover:text-accent-text"
                    >
                      <span className="w-[26px] h-[26px] shrink-0 rounded-full bg-tint text-accent-text overflow-hidden inline-flex items-center justify-center font-CircularStdBlack" style={{ fontSize: 10 }}>
                        {person.avatar ? (
                          <img
                            src={cldUrl(person.avatar, { w: 52, h: 52, crop: "fill", gravity: "face" })}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          (person.displayName || "")
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()
                        )}
                      </span>
                      <span className="truncate">{person.displayName}</span>
                    </button>
                  ))}
                  {/* The old header rendered videoPros[0] and dropped the rest;
                      live videos carry up to four. Three plus a count, and the
                      grid's `items-start` means a growing EXPERTS cell only
                      lengthens its own column — it never pushes FILE or DATES
                      down (handoff §10). */}
                  {experts.length > 3 && (
                    <span
                      className="inline-flex items-center h-5 px-2 rounded-full bg-surface-2 text-text-2 shrink-0"
                      style={{ fontSize: 11 }}
                    >
                      +{experts.length - 3}
                    </span>
                  )}
                </div>
              )}
            </MetaCell>

            <MetaCell label="Content type">
              {contentTypeLabel ? (
                <span className="text-text-1">{contentTypeLabel}</span>
              ) : (
                <EmptyValue />
              )}
            </MetaCell>

            <MetaCell label="Channel">
              {channelLabel ? (
                <span className="text-text-1">{channelLabel}</span>
              ) : (
                <EmptyValue />
              )}
            </MetaCell>

            <MetaCell label="File">
              {fileLine || formatVideoFileSize(video.file_size) ? (
                <span className="text-text-1 tabular-nums inline-flex items-center flex-wrap gap-x-2">
                  {fileLine}
                  {fileLine && formatVideoFileSize(video.file_size) ? <span>·</span> : null}
                  <FileSizeSpec size={video.file_size} />
                </span>
              ) : (
                <EmptyValue />
              )}
            </MetaCell>

            <MetaCell label="Dates">
              <span className="text-text-1 tabular-nums">
                Delivered {deliveredDate || "—"}
                {"  ·  "}
                Approved {approvalDate || "—"}
              </span>
            </MetaCell>

            {/* Only when the project asked for variants. Same chips as the
                project video card, so a "Watermarks: TDC Watermark" reads
                the same here as in the Downloads tab. Spans the card: option
                names are long and a half-width column would wrap every chip. */}
            {optionGroups.length > 0 && (
              <div className="sm:col-span-2">
                <MetaCell label="Options">
                  <div className="flex flex-wrap gap-1.5">
                    {optionGroups.map((g) => (
                      <span
                        key={g.optionId}
                        className="inline-flex items-center h-[22px] px-[9px] rounded-full bg-surface-2 text-text-1"
                        style={{ fontSize: 11 }}
                      >
                        {g.optionName}: {g.displayValue}
                      </span>
                    ))}
                  </div>
                </MetaCell>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* A full-width row between the identity block and the first panel, NOT
          inside the identity column: at 1280 that column is 384px and the label
          plus the chips need roughly 520px. It never wraps to a second line —
          the label drops first, then the chips scroll. */}
      <div
        className="flex items-center gap-2 flex-nowrap overflow-x-auto -mx-1 px-1"
        style={{ scrollbarWidth: "none" }}
      >
        <span className={`${MICRO} text-muted mr-0.5 shrink-0 hidden xl:inline`} style={MICRO_STYLE}>
              On this page
            </span>
            {jump.map((chip) => {
              const isActive = activeChip === chip.id;
              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => goToPanel(chip.id)}
                  aria-current={isActive ? "true" : undefined}
                  className={`inline-flex items-center gap-[7px] h-7 px-3 rounded-full border shrink-0 transition-colors ${
                    isActive
                      ? "bg-text-1 border-transparent text-surface"
                      : "bg-surface border-border-token text-text-1 hover:border-accent"
                  }`}
                  style={{ fontSize: 12 }}
                >
                  {chip.label}
                  {chip.count != null && (
                    <span className={isActive ? "opacity-70 tabular-nums" : "text-muted tabular-nums"}>
                      {chip.count}
                    </span>
                  )}
                </button>
              );
            })}
      </div>
    </div>
  );
}
